import * as React from "react";
import {
  Box,
  Grid,
  Divider,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button as MUIButton,
} from "@mui/material";
import { connect } from "react-redux";
import {
  NavLink,
  useNavigate,
  useParams
} from "react-router-dom";
import {
  cancelOrder,
  getOrders,
  sendOrderConfirmation,
  sendOrderSdd,
  sendOrderDefaultShipping,
} from "../app/store/actions/dataActions";
import { useEffect } from "react";
import { Schedule } from "@mui/icons-material";
import moment from "moment";
import { useTranslation } from "react-i18next";
import CreateTicketModal from "../app/components/CreateTicketModal";
import { isEligibleForSendConfirmation } from "../utils/confirmIt";
import {
  isEligibleForSendSdd,
  isEligibleForSendDefaultShipping,
  isOnBlinkSdd,
  isAfterSameDayCutoff,
} from "../utils/sameDay";
import { getOrderCourierLabel, getOrderShippingServiceLabel, getOrderTrackingId } from "../app/utils/shippingDisplay";
import ReturnInboundSection from "../app/components/ReturnInboundSection";
import Hoverable from "../app/glass/Hoverable";
import { cardShell, StatusPill } from "../app/glass/styles";
import { orderPickedupAt } from "../app/glass/OrderTableCells";
import { main_button } from "../app/components/Styles";

const chipDisabledStyle = (disabled) => (
  disabled ? { opacity: 0.45, cursor: "not-allowed" } : undefined
);

const Field = ({ label, children, style }) => (
  <div style={style}>
    <div style={{ fontSize: 11.5, color: "var(--mut)", marginBottom: 5 }}>{label}</div>
    <div style={{ fontSize: 14.5, fontWeight: 500, color: "var(--tx)" }}>{children}</div>
  </div>
);

export const Order = ({
  getOrders,
  user,
  order,
  cancelOrder,
  sendOrderConfirmation,
  sendOrderSdd,
  sendOrderDefaultShipping,
  isLoading,
  isWaiting,
  token,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [helpTicketOpen, setHelpTicketOpen] = React.useState(false);
  const [helpTicketContext, setHelpTicketContext] = React.useState(null);
  const [shippingSwitchConfirm, setShippingSwitchConfirm] = React.useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    getOrders({ orderId: id }, navigate)
  }, [id])

  // Calculate net total for transactions
  const netTotal = React.useMemo(() => {
    if (!order?.transactions || order.transactions.length === 0) return 0;
    return order.transactions.reduce((sum, tx) => {
      const value = parseFloat(tx.value || 0);
      if (tx.type === 'COD') {
        return sum + value; // COD can be positive or negative (refund)
      } else if (tx.type === 'Compensation') {
        return sum + value; // Compensation is credit
      } else if (['Fee', 'ShippingFee'].includes(tx.type)) {
        return sum - value; // Fees are debit
      }
      return sum + value;
    }, 0);
  }, [order?.transactions]);

  const getActorLabel = React.useCallback((actorValue, fallbackKey = "partner") => {
    const normalizedValue = String(actorValue || "").trim().toLowerCase();

    if (normalizedValue === "customer") return t("orderDetails.actors.customer");
    if (normalizedValue === "partner") return t("orderDetails.actors.partner");
    if (normalizedValue === "confirmit") return t("orderDetails.actors.confirmit");
    if (normalizedValue === "admin") return t("orderDetails.actors.admin");

    if (actorValue) return actorValue;
    return t(`orderDetails.actors.${fallbackKey}`);
  }, [t]);

  const canSendConfirmation = isEligibleForSendConfirmation(order, user);
  const canSendSdd = isEligibleForSendSdd(order);
  const canSendDefaultShipping = isEligibleForSendDefaultShipping(order);
  const orderOnBlink = isOnBlinkSdd(order);
  const brandShippingService =
    user?.vendor?.shippingService || order?.vendor?.shippingService || t("orders.brandDefaultShipping");
  const pickedupAt = orderPickedupAt(order);

  const startReturnDisabled = order?.status !== "Delivered";
  const startExchangeDisabled = order?.status !== "Delivered" && order?.status !== "Returned to Chainz" && order?.status !== "Returned";
  const cancelDisabled = order?.status === "Cancelled" || isLoading || isWaiting;
  const sendConfirmationDisabled = isLoading || isWaiting;
  const shippingSwitchDisabled = isLoading || isWaiting;
  const editShippingDisabled = !["New", "Sold Out"].includes(order?.status);

  const openShippingSwitchConfirm = (direction) => {
    if (shippingSwitchDisabled) return;
    setShippingSwitchConfirm(direction);
  };

  const closeShippingSwitchConfirm = () => {
    if (isWaiting) return;
    setShippingSwitchConfirm(null);
  };

  const confirmShippingSwitch = () => {
    if (!shippingSwitchConfirm || shippingSwitchDisabled) return;
    const refreshQuery = { orderId: id };
    if (shippingSwitchConfirm === "sdd") {
      sendOrderSdd({ orderId: id }, refreshQuery);
    } else {
      sendOrderDefaultShipping({ orderId: id }, refreshQuery);
    }
    setShippingSwitchConfirm(null);
  };

  const shippingSwitchConfirmCopy =
    shippingSwitchConfirm === "sdd"
      ? t("orders.sendToSddConfirm", { carrier: "Blink same-day delivery" })
      : t("orders.sendToDefaultShippingConfirm", { carrier: brandShippingService });

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* Header Section */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <Hoverable
            as="button"
            type="button"
            className="cz-chip"
            onClick={() => navigate(-1)}
            hover={{}}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ transform: isRTL ? "scaleX(-1)" : undefined }}
            >
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </svg>
            {t("common.back")}
          </Hoverable>
          <div style={{ fontSize: 22, fontWeight: 600, color: "var(--tx)" }}>
            {order?.isReturn ? t("orderDetails.returnOrderDetailsTitle") : t("orderDetails.orderDetailsTitle")}
          </div>
          {order?.vendor?.name && (
            <div style={{ marginInlineStart: "auto", fontSize: 13, color: "var(--mut)" }}>
              {t("orderDetails.vendor")}: {order.vendor.name}
            </div>
          )}
        </div>

        {/* Loading State */}
        {isLoading ? (
          <Box sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            flexDirection: "column",
            gap: 2
          }}>
            <CircularProgress sx={{ color: "var(--red-tx)" }} />
            <div style={{ fontSize: 14, color: "var(--mut)" }}>
              {t("orderDetails.fetchingOrder")}
            </div>
          </Box>
        ) : (
          <>
            {/* Action Buttons */}
            <div
              style={{
                ...cardShell,
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 18px",
                borderRadius: 20,
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", flex: 1 }}>
                {!order?.isReturn && (
                  <button
                    type="button"
                    className="cz-chip"
                    disabled={startReturnDisabled}
                    style={chipDisabledStyle(startReturnDisabled)}
                    onClick={() => navigate(`/orders/return/${id}`)}
                  >
                    {t("orderDetails.startReturn")}
                  </button>
                )}
                {!order?.isReturn && (
                  <button
                    type="button"
                    className="cz-chip"
                    disabled={startExchangeDisabled}
                    style={chipDisabledStyle(startExchangeDisabled)}
                    onClick={() => navigate(`/orders/exchange/${id}`)}
                  >
                    {t("orderDetails.startExchange")}
                  </button>
                )}
                {["New", "Sold Out", "Preparing", "Prepared", "Pending Confirmation", "Unreachable"].includes(order?.status) && (
                  <button
                    type="button"
                    className="cz-chip red"
                    onClick={() => cancelOrder({orderId: id}, navigate)}
                    disabled={cancelDisabled}
                    style={chipDisabledStyle(cancelDisabled)}
                  >
                    {isWaiting ? t("orderDetails.cancelling") : isLoading ? t("orderDetails.fetchingOrder") : t("orders.cancelOrder")}
                  </button>
                )}
                {canSendConfirmation && (
                  <button
                    type="button"
                    className="cz-chip"
                    onClick={() => sendOrderConfirmation({ orderId: id }, { orderId: id })}
                    disabled={sendConfirmationDisabled}
                    style={chipDisabledStyle(sendConfirmationDisabled)}
                  >
                    {isWaiting ? t("orderDetails.sendingConfirmation") : t("orders.sendConfirmation")}
                  </button>
                )}
                {canSendSdd && (
                  <button
                    type="button"
                    className="cz-chip"
                    title={t("orders.sendToSddHelp")}
                    onClick={() => openShippingSwitchConfirm("sdd")}
                    disabled={shippingSwitchDisabled}
                    style={chipDisabledStyle(shippingSwitchDisabled)}
                  >
                    {isWaiting ? t("orders.sendingToSdd") : t("orders.sendToSdd")}
                  </button>
                )}
                {canSendDefaultShipping && (
                  <button
                    type="button"
                    className="cz-chip"
                    title={t("orders.sendToDefaultShippingHelp", { carrier: brandShippingService })}
                    onClick={() => openShippingSwitchConfirm("default")}
                    disabled={shippingSwitchDisabled}
                    style={chipDisabledStyle(shippingSwitchDisabled)}
                  >
                    {isWaiting ? t("orders.sendingToDefaultShipping") : t("orders.sendToDefaultShipping")}
                  </button>
                )}
              </div>
              <div style={{ marginInlineStart: "auto", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {!order?.isReturn && (
                  <button
                    type="button"
                    className="cz-chip"
                    onClick={() => navigate(`/orders/duplicate/${id}`)}
                  >
                    {t("orderDetails.duplicateOrder")}
                  </button>
                )}
                <button
                  type="button"
                  className="cz-chip"
                  disabled={editShippingDisabled}
                  style={chipDisabledStyle(editShippingDisabled)}
                  onClick={() => navigate(`/orders/fulfillment/${order?._id}`)}
                >
                  {t("actionCenter.editShipping")}
                </button>
                <button
                  type="button"
                  className="cz-chip red"
                  onClick={() => { setHelpTicketContext({ ticketType: "Order", relevantOrder: id }); setHelpTicketOpen(true); }}
                >
                  {t("helpDesk.getHelp")}
                </button>
              </div>
            </div>

        {/* Order History & ETA - Compact Horizontal */}
        {(order?.orderHistory?.length > 0 || order?.eta) && (
          <div style={cardShell}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              {/* ETA Display */}
              {order?.eta && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: "12px 16px",
                    background: "var(--card-solid)",
                    borderRadius: 16,
                    border: "1px solid rgba(190,50,62,0.35)",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                    maxWidth: 400
                  }}
                >
                  <Schedule sx={{ color: "var(--red-tx3)", fontSize: 20 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11.5, color: "var(--mut)", marginBottom: 2 }}>
                      {t("orderDetails.estimatedDelivery")}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--tx)" }}>
                      {moment(order.eta).format("MMM D, YYYY h:mm A")}
                    </div>
                  </div>
                </div>
              )}

              {/* Order History - Horizontal Timeline */}
              {order?.orderHistory && order.orderHistory.length > 0 && (
                <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ fontSize: 12, color: "var(--mut2)", marginBottom: 16, textAlign: "center" }}>
                    {t("orderDetails.orderHistory")}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                      overflowX: "auto",
                      width: "100%",
                      flexWrap: "wrap",
                    }}
                  >
                    {(() => {
                      const filteredHistory = order.orderHistory.filter(
                        (historyItem) => !order?.isReturn || historyItem.status !== "Prepared"
                      );
                      return filteredHistory.map((historyItem, index) => {
                        const isLast = index === filteredHistory.length - 1;
                        const isCompleted = historyItem.done;
                        const nextItem = !isLast ? filteredHistory[index + 1] : null;
                        const nextIsCompleted = nextItem?.done;

                      return (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            flexShrink: 0
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "8px 16px",
                              borderRadius: 999,
                              background: isCompleted ? "rgba(125,1,12,0.35)" : "var(--s04)",
                              border: `1px solid ${isCompleted ? "rgba(190,50,62,0.45)" : "var(--s10)"}`,
                            }}
                          >
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: isCompleted ? "#c0303e" : "var(--s25)",
                              }}
                            />
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: isCompleted ? "var(--red-tx)" : "var(--mut)" }}>
                                {historyItem.status}
                              </div>
                              {historyItem.date && (
                                <div style={{ fontSize: 10, color: "var(--mut)", marginTop: 1 }}>
                                  {moment(historyItem.date).format("MMM D, h:mm A")}
                                </div>
                              )}
                            </div>
                          </div>

                          {!isLast && (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke={isCompleted && nextIsCompleted ? "var(--red-tx3)" : "var(--mut)"}
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                opacity: isCompleted && nextIsCompleted ? 1 : 0.4,
                                transform: isRTL ? "scaleX(-1)" : undefined,
                              }}
                            >
                              <path d="M5 12h14" />
                              <path d="m13 6 6 6-6 6" />
                            </svg>
                          )}
                        </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="cz-g1" style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 20, alignItems: "start" }}>
          {/* Left Column - Order Info & Transactions */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
            {/* Order Information */}
            <div style={{ ...cardShell, display: "flex", flexDirection: "column", gap: 18 }}>
                <div style={{ fontSize: 17, fontWeight: 600, color: "var(--tx)" }}>
                  {t("orderDetails.orderInformation")}
                </div>
                <Field label={t("orderDetails.orderId")}>{id}</Field>
                  {order?.shopifyOrderNumber && (
                    <Field label={t("orderDetails.shopifyOrderNumber")}>{order.shopifyOrderNumber}</Field>
                  )}
                  <div>
                    <div style={{ fontSize: 11.5, color: "var(--mut)", marginBottom: 7 }}>
                      {t("common.status")}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <StatusPill status={order?.status}>
                          {order?.status?.toUpperCase() || t("common.na")}
                        </StatusPill>
                        {order?.chainzFinanceStatus && (
                          <span style={{ fontSize: 12, color: "var(--mut)" }}>
                            {order.chainzFinanceStatus.toUpperCase()}
                          </span>
                        )}
                    </div>
                  </div>
                  {order?.createdAt && (
                    <Field label={t("orderDetails.createdAt")}>{moment(order.createdAt).format("lll")}</Field>
                  )}
                  {order?.confirmedAt && order?.status !== "Cancelled" && !order?.cancelledAt && (
                    <Field label={t("orderDetails.confirmedAt")}>
                        {moment(order.confirmedAt).format("lll")}
                        {" · "}
                        <span style={{ fontSize: 12, color: "var(--mut)", fontWeight: 400 }}>
                          {t("orderDetails.by", { actor: getActorLabel("customer") })}
                        </span>
                    </Field>
                  )}
                  {order?.cancelledAt && (
                    <Field label={t("orderDetails.cancelledAt")}>
                        {moment(order.cancelledAt).format("lll")}
                        {" · "}
                        <span style={{ fontSize: 12, color: "var(--mut)", fontWeight: 400 }}>
                          {t("orderDetails.by", { actor: getActorLabel(order?.cancelledBy) })}
                        </span>
                    </Field>
                  )}
                  {order?.assignedAt && (
                    <Field label={t("orderDetails.assignedAt")}>{moment(order.assignedAt).format("lll")}</Field>
                  )}
                  {order?.preparedAt && !order?.isReturn && (
                    <Field label={t("orderDetails.packedAt")}>{moment(order.preparedAt).format("lll")}</Field>
                  )}
                  {pickedupAt && (
                    <Field label={t("orderDetails.pickedUpAt")}>
                      {moment(pickedupAt).format("lll")}
                    </Field>
                  )}
                  {order?.deliveredAt && (
                    <Field label={t("orderDetails.deliveredAt")}>{moment(order.deliveredAt).format("lll")}</Field>
                  )}
                  {getOrderTrackingId(order) && (
                    <Field label={t("orders.trackingId")}>{getOrderTrackingId(order)}</Field>
                  )}
                  {getOrderShippingServiceLabel(order, user?.vendor) && (
                    <Field label={t("fulfillment.shippingService")}>{getOrderShippingServiceLabel(order, user?.vendor)}</Field>
                  )}
                  {getOrderCourierLabel(order, user?.vendor) && (
                    <Field label={t("fulfillment.courier")}>{getOrderCourierLabel(order, user?.vendor)}</Field>
                  )}
                  {orderOnBlink && (
                    <Field label={t("orders.sameDayDelivery")}>
                      <StatusPill status="Confirmed">Blink SDD</StatusPill>
                    </Field>
                  )}
                  {order?.fincartCode && (
                    <Field label="Fincart Code">{order.fincartCode}</Field>
                  )}
            </div>

            {/* Transactions */}
            {order?.transactions && order.transactions.length > 0 && (
              <div style={cardShell}>
                  <div style={{ fontSize: 17, fontWeight: 600, color: "var(--tx)", marginBottom: 16 }}>
                    {t("orderDetails.transactionBreakdown")}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {[...order.transactions].sort((a, b) => {
                      // Sort COD first, then others by date
                      if (a.type === 'COD' && b.type !== 'COD') return -1;
                      if (a.type !== 'COD' && b.type === 'COD') return 1;
                      const dateA = a.createdAt ? moment(a.createdAt) : moment(0);
                      const dateB = b.createdAt ? moment(b.createdAt) : moment(0);
                      return dateA.isBefore(dateB) ? -1 : 1;
                    }).map((tx, index) => {
                      const value = parseFloat(tx.value || 0);
                      let isCredit = false;
                      let isDebit = false;

                      if (tx.type === 'COD') {
                        isCredit = value >= 0;
                        isDebit = value < 0;
                      } else if (tx.type === 'Compensation') {
                        isCredit = true;
                      } else if (['Fee', 'ShippingFee'].includes(tx.type)) {
                        isDebit = true;
                      }

                      return (
                        <Hoverable
                          key={tx._id || index}
                          base={{
                            padding: 16,
                            background: "var(--s04)",
                            borderRadius: 16,
                            border: "1px solid var(--s08)",
                            borderLeft: `4px solid ${isCredit ? '#7dcea0' : isDebit ? 'rgba(190,50,62,0.85)' : 'var(--s14)'}`,
                          }}
                          hover={{ background: "var(--s06)" }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Field label={t("common.type")}>
                              {tx.type || t("common.na")}
                              {tx.type === 'COD' && value < 0 && t("transactions.refundParen")}
                              </Field>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <div style={{ fontSize: 11.5, color: "var(--mut)", marginBottom: 5 }}>
                                {t("common.amount")}
                              </div>
                              <div
                                style={{
                                  fontSize: 14.5,
                                  fontWeight: 600,
                                  color: isCredit ? "#7dcea0" : isDebit ? "var(--red-tx2)" : "var(--tx)",
                                }}
                              >
                                {isCredit ? '+' : isDebit ? '-' : ''}
                                {Math.abs(value).toFixed(2)} EGP
                              </div>
                            </Grid>
                            {tx.service && tx.service !== 'NONE' && (
                              <Grid item xs={12} sm={6}>
                                <Field label={t("transactions.table.service")}>{tx.service}</Field>
                              </Grid>
                            )}
                            {tx.createdAt && (
                              <Grid item xs={12} sm={6}>
                                <Field label={t("common.date")}>{moment(tx.createdAt).format("lll")}</Field>
                              </Grid>
                            )}
                            {tx.cashoutId && (
                              <Grid item xs={12}>
                                <Field label={t("transactions.cashoutId")}>{tx.cashoutId}</Field>
                              </Grid>
                            )}
                          </Grid>
                        </Hoverable>
                      );
                    })}
                  </div>

                  {/* Net Total */}
                  <div style={{
                    marginTop: 20,
                    padding: 16,
                    background: "var(--s04)",
                    borderRadius: 16,
                    border: "1px solid rgba(190,50,62,0.35)",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 600, color: "var(--tx)" }}>
                        {t("orderDetails.netAmount")}
                      </div>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: netTotal >= 0 ? "#7dcea0" : "var(--red-tx2)",
                        }}
                      >
                        {`${netTotal >= 0 ? '+' : ''}${netTotal.toFixed(2)} EGP`}
                      </div>
                    </div>
                  </div>
              </div>
            )}
          </div>

          {/* Right Column - Shipping Details & Items */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
            {/* Shipping Details */}
            <div
              style={{
                ...cardShell,
                background: "var(--card-solid)",
                border: "1px solid rgba(190,50,62,0.35)",
              }}
            >
                <div style={{ fontSize: 17, fontWeight: 600, color: "var(--tx)", marginBottom: 18 }}>
                  {t("orderDetails.shippingDetails")}
                </div>
                <div className="cz-g2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                  {order?.orderFulfillment?.customer && (
                    <Field label={t("orders.customerName")}>{order.orderFulfillment.customer}</Field>
                  )}
                  {order?.orderFulfillment?.phone && (
                    <div>
                      <div style={{ fontSize: 11.5, color: "var(--mut)", marginBottom: 5 }}>
                        {t("orderDetails.phoneNumber")}
                      </div>
                      <NavLink to={`tel:${order.orderFulfillment.phone}`} style={{ color: "var(--red-tx3)", textDecoration: "none", fontSize: 14.5, fontWeight: 500 }}>
                          {order.orderFulfillment.phone}
                        </NavLink>
                    </div>
                  )}
                  {order?.orderFulfillment?.email && (
                    <div>
                      <div style={{ fontSize: 11.5, color: "var(--mut)", marginBottom: 5 }}>
                        Email
                      </div>
                      <NavLink to={`mailto:${order.orderFulfillment.email}`} style={{ color: "var(--red-tx3)", textDecoration: "none", fontSize: 14.5, fontWeight: 500 }}>
                          {order.orderFulfillment.email}
                        </NavLink>
                    </div>
                  )}
                  {order?.orderFulfillment?.shippingAddress?.address1 && (
                    <Field label={t("orderDetails.addressLine1")} style={{ gridColumn: "1 / -1" }}>
                        {order.orderFulfillment.shippingAddress.address1}
                    </Field>
                  )}
                  {order?.orderFulfillment?.shippingAddress?.address2 && (
                    <Field label={t("orderDetails.addressLine2")} style={{ gridColumn: "1 / -1" }}>
                        {order.orderFulfillment.shippingAddress.address2}
                    </Field>
                  )}
                  {order?.orderFulfillment?.shippingAddress?.city && (
                    <Field label={t("orderDetails.city")}>
                        {order.orderFulfillment.shippingAddress.city}
                    </Field>
                  )}
                  {order?.orderFulfillment?.shippingAddress?.province && (
                    <Field label={t("orderDetails.province")}>
                        {order.orderFulfillment.shippingAddress.province}
                    </Field>
                  )}
                  {(order?.orderFulfillment?.shippingAddress?.country || order?.orderFulfillment?.shippingAddress?.country_code) && (
                    <Field label={t("orderDetails.country")}>
                        {order.orderFulfillment.shippingAddress.country || order.orderFulfillment.shippingAddress.country_code}
                    </Field>
                  )}
                  {order?.orderFulfillment?.price && (
                    <Field label={t("orderDetails.totalPrice")}>
                      <span style={{ fontWeight: 600 }}>{parseFloat(order.orderFulfillment.price).toFixed(2)} EGP</span>
                    </Field>
                  )}
                </div>

                {/* Bosta Exceptions */}
                {(order?.bostaExceptions?.length > 0 || order?.fincartExceptions?.length > 0) && (
                  <>
                    <Divider sx={{ my: 3, borderColor: "var(--s10)" }} />
                    <div>
                      <div style={{ fontSize: 17, fontWeight: 600, color: "var(--tx)", marginBottom: 16 }}>
                        {t("actionCenter.shippingIssues")}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {(order.bostaExceptions || order.fincartExceptions || []).map((exception, idx) => (
                          <div
                            key={idx}
                            style={{
                              padding: 16,
                              background: "var(--s04)",
                              borderRadius: 16,
                              borderLeft: "4px solid rgba(190,50,62,0.85)",
                            }}
                          >
                            <div style={{ fontSize: 11.5, color: "var(--mut)", marginBottom: 5 }}>
                              {t("orderDetails.issue")} #{idx + 1}
                            </div>
                            <div style={{ fontSize: 14.5, fontWeight: 500, color: "var(--tx)", marginBottom: 8 }}>
                              {exception.reason || exception}
                            </div>
                            {exception.date && (
                              <div style={{ fontSize: 12, color: "var(--mut)" }}>
                                {moment(exception.date).format("lll")}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
            </div>

            <ReturnInboundSection order={order} />

            {/* Order Items */}
            <div style={cardShell}>
                <div style={{ fontSize: 17, fontWeight: 600, color: "var(--tx)", marginBottom: 16 }}>
                  {t("orderDetails.orderItems")}
                </div>
                {isMobile ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {order?.orderItems && order.orderItems.length > 0 && order.orderItems.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          padding: 16,
                          background: item.isSoldOut ? 'rgba(125,1,12,0.18)' : "var(--s04)",
                          borderRadius: 16,
                          border: `1px solid ${item.isSoldOut ? 'rgba(190,50,62,0.35)' : 'var(--s08)'}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 14.5,
                            fontWeight: 600,
                            color: item.isSoldOut ? 'var(--red-tx2)' : "var(--tx)",
                            marginBottom: 8,
                          }}
                        >
                          {item.stockItem?.product?.name}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ fontSize: 13, color: "var(--mut)" }}>
                            {t("common.sku")}: {item.stockItem?.product?.sku || item.stockItem?.product?.externalId}
                          </div>
                          <div
                            style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: item.isSoldOut ? 'var(--red-tx2)' : "var(--tx)"
                            }}
                          >
                            {t("orderDetails.quantity")}: {item.quantityOrdered}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1.6fr 1fr 90px",
                        gap: 12,
                        fontSize: 11,
                        color: "var(--mut)",
                        padding: "0 4px 10px",
                        borderBottom: "1px solid var(--s07)",
                      }}
                    >
                        <div>{t("orderDetails.productName")}</div>
                        <div>{t("common.sku")}</div>
                        <div style={{ textAlign: "right" }}>{t("orderDetails.quantityOrdered")}</div>
                    </div>
                    <div>
                      {order?.orderItems && order.orderItems.length > 0 && order.orderItems.map((item, index) => (
                        <Hoverable
                          key={index}
                          base={{
                            display: "grid",
                            gridTemplateColumns: "1.6fr 1fr 90px",
                            gap: 12,
                            alignItems: "center",
                            padding: "13px 4px",
                            fontSize: 13.5,
                            borderBottom: "1px solid var(--s05)",
                            background: item.isSoldOut ? "rgba(125,1,12,0.12)" : "transparent",
                            borderRadius: item.isSoldOut ? 10 : 0,
                          }}
                          hover={{ background: item.isSoldOut ? "rgba(125,1,12,0.18)" : "var(--s06)" }}
                        >
                            <div style={{ fontWeight: 500, color: item.isSoldOut ? 'var(--red-tx2)' : "var(--tx)" }}>
                                {item.stockItem?.product?.name}
                            </div>
                            <div style={{ color: item.isSoldOut ? 'var(--red-tx2)' : "var(--mut2)", fontSize: 12.5 }}>
                                {item.stockItem?.product?.sku || item.stockItem?.product?.externalId}
                            </div>
                            <div style={{ textAlign: "right", fontWeight: 600, color: item.isSoldOut ? 'var(--red-tx2)' : "var(--tx)" }}>
                                {item.quantityOrdered}
                            </div>
                        </Hoverable>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
          </>
        )}
      </div>
      <Dialog
        open={Boolean(shippingSwitchConfirm)}
        onClose={closeShippingSwitchConfirm}
        maxWidth="xs"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "var(--appBg)",
            color: "var(--secColor4)",
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "var(--appBg)", color: "var(--secColor4)" }}>
          {shippingSwitchConfirm === "sdd"
            ? t("orders.sendToSdd")
            : t("orders.sendToDefaultShipping")}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "var(--appBg)", color: "var(--secColor4)" }}>
          <Typography sx={{ color: "var(--secColor4)", mt: 1 }}>
            {shippingSwitchConfirmCopy}
          </Typography>
          {shippingSwitchConfirm === "sdd" && isAfterSameDayCutoff() && (
            <Typography sx={{ color: "var(--secColor4)", mt: 1.5 }}>
              {t("orders.sameDayAfterCutoffWarning")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, backgroundColor: "var(--appBg)" }}>
          <MUIButton
            onClick={closeShippingSwitchConfirm}
            disabled={isWaiting}
            sx={{ color: "var(--secColor4)" }}
          >
            {t("common.cancel")}
          </MUIButton>
          <MUIButton
            onClick={confirmShippingSwitch}
            disabled={isWaiting}
            sx={{ ...main_button, textTransform: "none" }}
          >
            {isWaiting
              ? (shippingSwitchConfirm === "sdd"
                ? t("orders.sendingToSdd")
                : t("orders.sendingToDefaultShipping"))
              : t("common.confirm")}
          </MUIButton>
        </DialogActions>
      </Dialog>
      <CreateTicketModal
        open={helpTicketOpen}
        onClose={() => { setHelpTicketOpen(false); setHelpTicketContext(null); }}
        token={token}
        context={helpTicketContext}
        onSuccess={() => setHelpTicketOpen(false)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading,
  isWaiting: state?.wait?.isLoading,
  token: state?.auth?.token,
  order: state?.records?.order
});

const mapDispatchToProps = {
  getOrders,
  cancelOrder,
  sendOrderConfirmation,
  sendOrderSdd,
  sendOrderDefaultShipping,
};

export default connect(mapStateToProps, mapDispatchToProps)(Order);
